import api from './api';

export interface Task {
  id: string;
  number?: number;
  title: string;
  description?: string;
  projectId?: string;
  assigneeId?: string;
  creatorId: string;
  status: string; // Custom status per planner
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  dueDate?: string;
  tags?: string[];
  attachments?: string[];
  checklist?: Array<{ text: string; completed: boolean }>;
  swimlane?: string;
  position?: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  assignee?: {
    id: string;
    name: string;
    email: string;
    photoUrl?: string;
  };
  creator?: {
    id: string;
    name: string;
    email: string;
  };
  project?: {
    id: string;
    name: string;
  };
  comments?: Array<{
    id: string;
    content: string;
    userId: string;
    createdAt: string;
    user?: {
      id: string;
      name: string;
      photoUrl?: string;
    };
    mentions?: string[];
  }>;
  history?: Array<{
    id: string;
    action: string;
    field?: string;
    oldValue?: string;
    newValue?: string;
    createdAt: string;
    user?: {
      id: string;
      name: string;
      photoUrl?: string;
    };
  }>;
  commentsCount?: number;
  attachmentsCount?: number;
}

export interface PlannerPage {
  id: string;
  slug: string;
  name: string;
  description?: string;
  statuses: string[]; // Custom status columns
  wipLimits?: Record<string, number>;
  swimlanes?: Array<{ type: string; value: string }>;
  projectId?: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  members?: Array<{
    id: string;
    name: string;
    photoUrl?: string;
  }>;
}

export interface CreateTaskRequest {
  title: string;
  description?: string;
  projectId?: string;
  assigneeId?: string;
  status: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  dueDate?: string;
  tags?: string[];
  checklist?: Array<{ text: string; completed: boolean }>;
  plannerPageId?: string;
  swimlane?: string;
}

export interface UpdateTaskRequest extends Partial<CreateTaskRequest> {
  id: string;
}

// Get all tasks with filters
export const getTasks = async (params?: {
  plannerPageId?: string;
  projectId?: string;
  assigneeId?: string;
  status?: string;
  priority?: string;
  tags?: string[];
  search?: string;
  swimlane?: string;
}): Promise<Task[]> => {
  const queryParams = new URLSearchParams();
  if (params?.plannerPageId) queryParams.append('plannerPageId', params.plannerPageId);
  if (params?.projectId) queryParams.append('projectId', params.projectId);
  if (params?.assigneeId) queryParams.append('assigneeId', params.assigneeId);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.priority) queryParams.append('priority', params.priority);
  if (params?.swimlane) queryParams.append('swimlane', params.swimlane);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.tags && params.tags.length > 0) {
    params.tags.forEach(tag => queryParams.append('tags', tag));
  }

  const response = await api.get(`/api/tasks?${queryParams.toString()}`);
  return response.data.data || [];
};

// Get single task
export const getTask = async (id: string): Promise<Task> => {
  const response = await api.get(`/api/tasks/${id}`);
  return response.data.data;
};

// Create task
export const createTask = async (task: CreateTaskRequest): Promise<Task> => {
  const response = await api.post('/api/tasks', task);
  return response.data.data;
};

// Update task
export const updateTask = async (task: UpdateTaskRequest): Promise<Task> => {
  const { id, ...updateData } = task;
  const response = await api.put(`/api/tasks/${id}`, updateData);
  return response.data.data;
};

// Delete task
export const deleteTask = async (id: string): Promise<void> => {
  await api.delete(`/api/tasks/${id}`);
};

// Planner Pages
export const getPlannerPages = async (): Promise<PlannerPage[]> => {
  const response = await api.get('/api/planner-pages');
  return response.data.data || [];
};

export const createPlannerPage = async (page: {
  name: string;
  description?: string;
  statuses: string[];
  swimlanes?: Array<{ type: string; value: string }>;
  wipLimits?: Record<string, number>;
  projectId?: string;
  memberIds?: string[];
}): Promise<PlannerPage> => {
  const response = await api.post('/api/planner-pages', page);
  return response.data.data;
};

export const updatePlannerPage = async (id: string, page: Partial<PlannerPage> & { memberIds?: string[] }): Promise<PlannerPage> => {
  const response = await api.put(`/api/planner-pages/${id}`, page);
  return response.data.data;
};

export const deletePlannerPage = async (id: string): Promise<void> => {
  await api.delete(`/api/planner-pages/${id}`);
};
